import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A sticky shopping footer that appears after scrolling 110vh.
 * Displays product image and an add-to-cart button.
 *
 * @typedef {Object} StickyShoppingFooterRefs
 * @property {HTMLImageElement} productImage - The product image element
 * @property {HTMLInputElement} variantId - The hidden variant ID input
 * @property {HTMLElement} priceDisplay - The price display element
 *
 * @extends {Component<StickyShoppingFooterRefs>}
 */
class StickyShoppingFooterComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {boolean} */
  #pastTrigger = false;

  /** @type {boolean} */
  #inFooter = false;

  /** @type {boolean} */
  #scrollingDown = true;

  /** @type {number} */
  #lastScrollY = 0;

  /** @type {boolean} */
  #isMobile = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#setupScrollTrigger();
    this.#setupFooterObserver();
    this.#listenForVariantUpdates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#footerObserver?.disconnect();
    this.#abortController.abort();
  }

  #updateVisibility() {
    const visible = this.#pastTrigger && !this.#inFooter && (!this.#isMobile || this.#scrollingDown);
    if (visible) {
      this.removeAttribute('hidden');
    } else {
      this.setAttribute('hidden', '');
    }
  }

  #setupScrollTrigger() {
    this.#isMobile = window.matchMedia('(max-width: 749px)').matches;
    const threshold = window.innerHeight * (this.#isMobile ? 1.0 : 0.5);
    const { signal } = this.#abortController;

    const onScroll = () => {
      const currentY = window.scrollY;

      if (this.#isMobile) {
        this.#scrollingDown = currentY > this.#lastScrollY;
        this.#lastScrollY = currentY;
      }

      const past = currentY >= threshold;
      if (past !== this.#pastTrigger || this.#isMobile) {
        this.#pastTrigger = past;
        this.#updateVisibility();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    onScroll();
  }

  #setupFooterObserver() {
    const footer = document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer') || document.querySelector('[class*="footer"]');
    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#inFooter = entry.isIntersecting;
        }
        this.#updateVisibility();
      },
      { threshold: 0 }
    );

    this.#footerObserver.observe(footer);
  }

  /**
   * Formats a price in cents using the shop's money format.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.floor(cents / 100).toString();
    const amountWithComma = amount.replace('.', ',');

    return format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  #listenForVariantUpdates() {
    const { signal } = this.#abortController;
    const productId = this.querySelector('product-form-component')?.dataset.productId;
    const mainProductForm = [...document.querySelectorAll('#MainContent product-form-component')]
      .find((form) => !this.contains(form) && form.dataset.productId === productId);
    const productSection = mainProductForm?.closest('.shopify-section');

    productSection?.addEventListener(
      ThemeEvents.variantSelected,
      () => {
        const button = this.querySelector('[ref="addToCartButton"]');
        if (button) button.disabled = true;
      },
      { signal }
    );

    productSection?.addEventListener(
      ThemeEvents.variantUpdate,
      /** @param {*} event */
      (event) => {
        const variant = event.detail.resource;
        const button = this.querySelector('[ref="addToCartButton"]');
        const updatedButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

        // Use Shopify's rendered state, including inventory and quantity-rule restrictions.
        if (button) {
          button.disabled = !variant || variant.available === false || updatedButton?.disabled === true;
          const currentText = button.querySelector('.add-to-cart-text');
          const updatedText = updatedButton?.querySelector('.add-to-cart-text');
          if (currentText && updatedText) currentText.replaceWith(updatedText.cloneNode(true));
        }

        // Update variant ID
        if (this.refs.variantId) {
          this.refs.variantId.value = variant?.id ?? '';
        }

        if (!variant) return;

        // Update product image
        if (!this.hasAttribute('data-fixed-product-image') && this.refs.productImage && variant.featured_media?.preview_image?.src) {
          this.refs.productImage.src = variant.featured_media.preview_image.src + '&width=80';
          this.refs.productImage.srcset = '';
        }

        // Update price
        if (this.refs.priceDisplay && variant.price != null) {
          this.refs.priceDisplay.textContent = this.#formatMoney(variant.price);
        }
      },
      { signal }
    );
  }
}

if (!customElements.get('sticky-shopping-footer')) {
  customElements.define('sticky-shopping-footer', StickyShoppingFooterComponent);
}
